import toast from "react-hot-toast";

// Map the authenticated user's role (from the /verify-otp "account routing
// details") to a placeholder message. The real dashboards aren't built yet, so
// an already-registered user whose role doesn't match a known dashboard is NOT
// redirected — we surface a "Next to ..." message instead. Matching is
// keyword-based so it tolerates backend role variants (e.g. "club_student" /
// "Club Student", "coach" / "Coach", "independent").
export function rolePlaceholderMessage(role) {
  const r = String(role || "").toLowerCase();
  if (r.includes("coach")) return "Next to Coach Dashboard";
  if (r.includes("independent")) return "Next to Independent Student Dashboard";
  if (r.includes("club")) return "Next to Club Student Dashboard";
  return null;
}

// Shared post-OTP-verification routing. Both the /login page and the home-page
// login modal verify an OTP and then need to route the user identically, so the
// decision logic lives here once. `data` is the /verify-otp response ("account
// routing details"). Callbacks let each caller handle in-page outcomes instead
// of a hard navigate:
//   • onNotRegistered() — no account and nothing in progress; show the
//     "No Account Found" prompt.
//   • onPlaceholder(text) — registered + paid but role has no dashboard yet;
//     show a "Login successful" placeholder message.
//   • onClubSelect({ member, clubs }) — optional; a coach with 2+ clubs needs
//     to pick one before entering the dashboard. If omitted, falls back to
//     navigating to the standalone /select-club route (ClubSelectPage.jsx).
export function routeAfterVerify(data, { email, navigate, onNotRegistered, onPlaceholder, onClubSelect }) {
  // Resolve the user's role and whether the email is already registered,
  // tolerating different backend key names.
  const member = data?.member ?? null;
  const role =
    data?.role ??
    data?.member_type ??
    data?.user_type ??
    data?.type ??
    data?.account_type ??
    data?.user?.role ??
    member?.member_type;
  const registered =
    data?.account_exists ??
    data?.userExists ??
    data?.registered ??
    data?.exists ??
    data?.is_registered ??
    Boolean(member);

  // Not registered: this email has no account yet.
  if (!registered) {
    // A pending registration (email verified, payment not completed) has no
    // account row yet but /verify-otp returns its full registration process
    // (submitted details + payment status). Send them to the summary page to
    // review those details and complete payment, rather than to "register".
    const rp = data?.registration_process;
    if (rp?.process_id) {
      toast("Welcome back — complete your registration.", { icon: "📝" });
      navigate("/registration-status", { state: { registration: rp, email } });
      return;
    }
    // Genuinely no account and nothing in progress — prompt to register.
    onNotRegistered?.();
    toast.error("No account found. Please register to login.");
    return;
  }

  // ── Already registered ──────────────────────────────────────────────────
  // Branch on payment status:
  //   • Payment done  → send them to the relevant dashboard.
  //   • Payment pending → send them to the summary page so they can review
  //     their submitted details and complete payment.
  const paid = String(member?.payment_status || "").toLowerCase().includes("paid");

  if (!paid) {
    toast("Payment pending — review your details.", { icon: "💳" });
    navigate("/registration-status", { state: { member, email } });
    return;
  }

  // A coach who belongs to 2+ clubs — /verify-otp's redirect_to comes from
  // ClubCoachMembership::redirectForClubs on the backend ("coach_dashboard"
  // for 0/1 clubs, "club_select" for 2+). Send them to pick one before the
  // dashboard rather than silently landing on whichever club_id happens to
  // be stored, since that's meaningless once there's more than one.
  if (member && data?.redirect_to === "club_select") {
    const clubs = data?.clubs || [];
    if (onClubSelect) {
      onClubSelect({ member, clubs });
    } else {
      navigate("/select-club", { state: { member, clubs } });
    }
    return;
  }

  // Paid — route to the relevant dashboard for the member's role.
  const r = String(role || "").toLowerCase();
  const isCoach = data?.redirect_to === "coach_dashboard" || r.includes("coach");

  if (member && isCoach) {
    toast.success("Welcome back, Coach!");
    navigate("/coach-dashboard", { state: { member } });
    return;
  }
  if (member && r.includes("club")) {
    toast.success("Welcome back!");
    navigate("/student-dashboard", { state: { member } });
    return;
  }
  if (member && r.includes("independent")) {
    toast.success("Welcome back!");
    navigate("/independent-dashboard", { state: { member } });
    return;
  }

  // Unknown role — fall back to a placeholder message.
  const text = rolePlaceholderMessage(role) || "Login successful. Your dashboard is under development.";
  onPlaceholder?.(text);
  toast.success(text);
}
